import React, { ComponentType, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import InfoIcon from '@/assets/icons/info-small';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { appTheme } from '@/components/lib/theme';
import { useCurrencyOverviewModal } from '@/feature/account-details-sweeps/components/CurrencyOverviewModalProvider';
import { useUserStore } from '@/hooks/use-user-store';
import { designSystem } from '@/styles/styles';

import { CurrencyCardOverlay } from './CurrencyCardOverlay';
import { Currency, SocialCurrencyIconProps } from './utils';

type CurrencyCardProps = {
    title: string;
    touchpointText?: string;
    amount: string;
    description: string;
    backgroundColor: string;
    textColor: string;
    icon?: ComponentType<SocialCurrencyIconProps>;
    onPress?: () => void;
    isActive?: boolean;
    currency?: Currency;
};

const styles = StyleSheet.create({
    cardBase: {
        borderRadius: 20,
        borderCurve: 'continuous',
        padding: 16,
        height: '100%',
        width: '100%',
        shadowOffset: {
            width: 0,
            height: -2,
        },
        shadowColor: designSystem.colors.neutral900,
        shadowOpacity: 0.35,
        shadowRadius: 20,
        elevation: 8,
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
    },
    label: {
        fontFamily: 'SFMono-Regular',
        fontSize: 14,
        lineHeight: 20,
        fontWeight: '500',
        textTransform: 'uppercase',
    },
    touchpoint: {
        backgroundColor: ds.colorValue.background.transparent.subtle,
        borderRadius: appTheme.borderRadii.r7,
        borderCurve: 'continuous',
    },
    infoIcon: {
        marginLeft: 6,
    },
    text: {
        top: -4,
    },
});

export const CurrencyCard = ({
    title,
    touchpointText,
    amount,
    description,
    backgroundColor,
    textColor,
    icon,
    onPress,
    isActive,
    currency,
}: CurrencyCardProps) => {
    const { t } = useTranslation('common');
    const devMenuPresses = useRef<number>(0);
    const { showCurrencyOverviewModal } = useCurrencyOverviewModal();
    const navigation = useNavigation();
    const profile = useUserStore(state => state.profile);
    const isTouchpointAmount = !isNaN(Number(touchpointText?.replace(/[$,]/g, ''))) && touchpointText?.trim() !== '';

    const textColorStyle = { color: textColor };

    return (
        <TouchableOpacity
            style={[styles.cardBase, { backgroundColor }]}
            onPress={onPress}
            disabled={!onPress}
            activeOpacity={1}
        >
            {isActive ? <CurrencyCardOverlay /> : null}
            <Row justifyContent="space-between" alignItems="flex-start">
                <Row alignItems="center">
                    <TouchableOpacity
                        onPress={() => {
                            if (!profile.email.endsWith('@betr.app')) {
                                return;
                            }

                            devMenuPresses.current++;
                            if (devMenuPresses.current > 5) {
                                navigation.navigate('DeveloperScreen' as never);
                            }
                        }}
                    >
                        {icon ? React.createElement(icon, { size: 24 }) : null}
                    </TouchableOpacity>
                    <Text
                        fontFamily={'SFMono-Regular'}
                        ml="s8"
                        fontSize={16}
                        lineHeight={24}
                        fontWeight="500"
                        textTransform={'uppercase'}
                        style={textColorStyle}
                    >
                        {title}
                    </Text>
                </Row>
                {touchpointText ? (
                    <TouchableOpacity
                        style={styles.touchpoint}
                        onPress={() =>
                            !isTouchpointAmount &&
                            showCurrencyOverviewModal({
                                isTouchpoint: true,
                                currency: currency,
                            })
                        }
                        disabled={isTouchpointAmount}
                    >
                        <Row justifyContent={'center'} alignItems={'center'} px={'s8'} py={'s4'}>
                            <Text
                                {...(isTouchpointAmount
                                    ? {
                                          fontFamily: 'SFMono-Regular',
                                          fontSize: 14,
                                          lineHeight: 15,
                                          fontWeight: '500',
                                      }
                                    : { variant: 'labelMedium' as const })}
                                style={textColorStyle}
                            >
                                {touchpointText}
                            </Text>
                            {isTouchpointAmount ? null : (
                                <InfoIcon color={textColor} fillColor={textColor} style={styles.infoIcon} />
                            )}
                        </Row>
                    </TouchableOpacity>
                ) : null}
            </Row>

            <Box flex={1} justifyContent="space-between">
                <Text style={[styles.label, textColorStyle]} marginTop={'s20'}>
                    {t('balance')}
                </Text>
                <Text
                    fontFamily="SF-Mono-MediumMedium"
                    fontSize={40}
                    lineHeight={40}
                    letterSpacing={-2}
                    fontWeight="600"
                    style={[textColorStyle, styles.text]}
                >
                    {amount}
                </Text>
                <Text style={[styles.label, textColorStyle]}>{description}</Text>
            </Box>
        </TouchableOpacity>
    );
};
